import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { ScrollToTop } from "@/components/layout/ScrollToTop";
import { ChatBubble } from "@/components/chat/ChatBubble";
import { AuthProvider } from "@/hooks/useAuth";
import { usePageTracking } from "@/hooks/usePageTracking";

const Index = lazy(() => import("./pages/Index"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Projects = lazy(() => import("./pages/Projects"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));

const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Admin = lazy(() => import("./pages/Admin"));
const ServiceAreas = lazy(() => import("./pages/ServiceAreas"));
const CityPage = lazy(() => import("./pages/service-areas/CityPage"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Careers = lazy(() => import("./pages/Careers"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Lawn Care & Landscaping Service Pages
const LawnCare = lazy(() => import("./pages/services/LawnCare"));
const Aeration = lazy(() => import("./pages/services/Aeration"));
const Mulching = lazy(() => import("./pages/services/Mulching"));
const Dethatching = lazy(() => import("./pages/services/Dethatching"));
const SpringFallCleanups = lazy(() => import("./pages/services/SpringFallCleanups"));
const SnowRemoval = lazy(() => import("./pages/services/SnowRemoval"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <PageTracker />
            <ScrollToTop />
            <Routes>
              <Route path="/admin" element={
                <Suspense fallback={<div className="min-h-screen" />}>
                  <Admin />
                </Suspense>
              } />
              
              <Route path="*" element={
                <div className="flex flex-col min-h-screen">
                  <Navbar />
                  <div className="flex-1">
                    <Suspense fallback={<div className="min-h-screen" />}>
                      <Routes>
                        <Route path="/" element={<Index />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/projects" element={<Projects />} />
                        <Route path="/projects/:slug" element={<ProjectDetail />} />
                        {/* Testimonials hidden for now */}
                        <Route path="/blog" element={<Blog />} />
                        <Route path="/blog/:slug" element={<BlogPost />} />
                        <Route path="/service-areas" element={<ServiceAreas />} />
                        <Route path="/service-areas/:slug" element={<CityPage />} />
                        <Route path="/gallery" element={<Gallery />} />
                        <Route path="/careers" element={<Careers />} />
                        
                        {/* Lawn Care & Landscaping Services */}
                        <Route path="/services/lawn-care" element={<LawnCare />} />
                        <Route path="/services/aeration" element={<Aeration />} />
                        <Route path="/services/mulching" element={<Mulching />} />
                        <Route path="/services/dethatching" element={<Dethatching />} />
                        <Route path="/services/spring-fall-cleanups" element={<SpringFallCleanups />} />
                        <Route path="/services/snow-removal" element={<SnowRemoval />} />
                        
                        {/* Redirects for SEO-friendly URLs */}
                        <Route path="/about-us" element={<Navigate to="/about" replace />} />
                        <Route path="/reviews" element={<Navigate to="/" replace />} />
                        <Route path="/testimonials" element={<Navigate to="/" replace />} />
                        
                        {/* Redirect old/removed service routes */}
                        <Route path="/services/fertilization" element={<Navigate to="/services/lawn-care" replace />} />
                        <Route path="/services/hardscaping" element={<Navigate to="/services/mulching" replace />} />
                        <Route path="/services/planting" element={<Navigate to="/services/mulching" replace />} />
                        <Route path="/services/roof-replacement" element={<Navigate to="/services/lawn-care" replace />} />
                        <Route path="/services/roof-repair" element={<Navigate to="/services/lawn-care" replace />} />
                        <Route path="/services/siding-replacement" element={<Navigate to="/services/mulching" replace />} />
                        <Route path="/services/siding-repair" element={<Navigate to="/services/mulching" replace />} />
                        <Route path="/services/deck-installation" element={<Navigate to="/services/lawn-care" replace />} />
                        <Route path="/services/gutter-installation" element={<Navigate to="/services/lawn-care" replace />} />
                        <Route path="/services/gutter-repairs" element={<Navigate to="/services/lawn-care" replace />} />
                        <Route path="/roofing" element={<Navigate to="/services/lawn-care" replace />} />
                        <Route path="/siding" element={<Navigate to="/services/mulching" replace />} />
                        <Route path="/gutters" element={<Navigate to="/services/lawn-care" replace />} />
                        <Route path="/storm-damage" element={<Navigate to="/services/lawn-care" replace />} />
                        <Route path="/ice-storms" element={<Navigate to="/services/snow-removal" replace />} />
                        
                        {/* Service area city redirects */}
                        <Route path="/maple-grove-mn" element={<Navigate to="/service-areas/maple-grove" replace />} />
                        <Route path="/plymouth-mn" element={<Navigate to="/service-areas/plymouth" replace />} />
                        <Route path="/brooklyn-park-mn" element={<Navigate to="/service-areas/maple-grove" replace />} />
                        
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                  </div>
                  <Footer />
                  <ChatBubble />
                </div>
              } />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

function PageTracker() {
  usePageTracking();
  return null;
}

export default App;
